import { RECOM_PROXY_BASE_PATH } from '@/data/recom/constants';
import { recomPaths } from '@/data/recom/endpoints';
import { recomRequest } from '@/data/recom/http';
import type { RecomMethod } from '@/data/recom/endpoints';
import type { RecomQuery } from '@/data/recom/http';
import type {
	CouponEvaluateData,
	MergeUserSessionData,
	RecomIdentity,
	RecommendationParams,
	RecommendationResponse,
	RecommendationRuleValues,
	RecommendationType,
	RecentlyViewedData,
	RecentlyViewedItem,
	RecordUserEventsData,
	SubmitUserQuizData,
	PopularProductItem,
	PopularProductsData,
	TrendingProductItem,
	TrendingProductsData,
	UpdateUserProfileData,
	UpdateUserProfileRequest,
	UserEvent,
} from '@/data/recom/types';

export type RecomCallOptions = {
	signal?: AbortSignal;
	baseUrl?: string;
	timeoutMs?: number;
	headers?: Record<string, string>;
};

const call = <TData, TMeta = Record<string, never>>(
	options: RecomCallOptions,
	method: RecomMethod,
	path: string,
	body?: unknown,
	query?: RecomQuery
) =>
	recomRequest<TData, TMeta>({
		baseUrl: options.baseUrl ?? RECOM_PROXY_BASE_PATH,
		path,
		method,
		body,
		query,
		signal: options.signal,
		timeoutMs: options.timeoutMs,
		headers: options.headers,
	});

export const resolveRecomIdentity = ({ userId, sessionId }: RecomIdentity): RecomIdentity =>
	userId ? { userId } : sessionId ? { sessionId } : {};

export const guestUserId = (sessionId: string): string => `guest:${sessionId}`;

export type GetRecommendationOptions = RecomCallOptions & {
	type: RecommendationType[];
	params?: Partial<Omit<RecommendationParams, 'storeId'>>;
	filters?: RecommendationRuleValues;
	priority?: RecommendationRuleValues;
	skipCuration?: boolean;
};

export const getRecommendation = ({
	type,
	params,
	filters,
	priority,
	skipCuration,
	...options
}: GetRecommendationOptions): Promise<RecommendationResponse> =>
	call<RecommendationResponse['data'], RecommendationResponse['meta']>(
		options,
		'POST',
		recomPaths.recommendation(),
		{
			type,
			rules: {
				params: { ...params, queries: params?.queries ?? [] },
				filters,
				priority,
			},
			skipCuration,
		}
	);

export type GetTrendingProductsOptions = RecomCallOptions & {
	limit?: number;
};

export const getTrendingProducts = async ({
	limit,
	...options
}: GetTrendingProductsOptions): Promise<TrendingProductItem[]> => {
	const { data } = await call<TrendingProductsData>(
		options,
		'GET',
		recomPaths.trending(),
		undefined,
		{ limit }
	);
	return data?.items ?? [];
};

export type GetPopularProductsOptions = RecomCallOptions & {
	limit?: number;
	windowDays?: number;
};

export const getPopularProducts = async ({
	limit,
	windowDays,
	...options
}: GetPopularProductsOptions): Promise<PopularProductItem[]> => {
	const { data } = await call<PopularProductsData>(
		options,
		'GET',
		recomPaths.popular(),
		undefined,
		{ limit, windowDays }
	);
	return data?.items ?? [];
};

export type RecordUserEventsOptions = RecomCallOptions & {
	identity: RecomIdentity;
	events: UserEvent[];
};

export const recordUserEvents = async ({
	identity,
	events,
	...options
}: RecordUserEventsOptions): Promise<RecordUserEventsData> => {
	const { data } = await call<RecordUserEventsData>(options, 'POST', recomPaths.userEvents(), {
		...resolveRecomIdentity(identity),
		events,
	});
	return data;
};

export type GetRecentlyViewedOptions = RecomCallOptions & {
	identity: RecomIdentity;
	limit?: number;
};

export const getRecentlyViewed = async ({
	identity,
	limit,
	...options
}: GetRecentlyViewedOptions): Promise<RecentlyViewedItem[]> => {
	const { data } = await call<RecentlyViewedData>(options, 'POST', recomPaths.recentlyViewed(), {
		...resolveRecomIdentity(identity),
		limit,
	});
	return data?.items ?? [];
};

export type MergeUserSessionOptions = RecomCallOptions & {
	userId: string;
	sessionId: string;
};

export const mergeUserSession = async ({
	userId,
	sessionId,
	...options
}: MergeUserSessionOptions): Promise<MergeUserSessionData> => {
	const { data } = await call<MergeUserSessionData>(options, 'POST', recomPaths.mergeSession(), {
		userId,
		sessionId,
	});
	return data;
};

export type SubmitUserQuizOptions = RecomCallOptions & {
	userId: string;
	sports: string[];
	brands: string[];
	gender?: string;
	sizes?: unknown;
};

export const submitUserQuiz = async ({
	userId,
	sports,
	brands,
	gender,
	sizes,
	...options
}: SubmitUserQuizOptions): Promise<SubmitUserQuizData> => {
	const { data } = await call<SubmitUserQuizData>(options, 'POST', recomPaths.quiz(), {
		userId,
		sports,
		brands,
		gender,
		sizes,
	});
	return data;
};

export type UpdateUserProfileOptions = RecomCallOptions & {
	userId: string;
	profile: UpdateUserProfileRequest;
};

export const updateUserProfile = async ({
	userId,
	profile,
	...options
}: UpdateUserProfileOptions): Promise<UpdateUserProfileData> => {
	const { data } = await call<UpdateUserProfileData>(
		options,
		'PUT',
		recomPaths.profile(userId),
		profile
	);
	return data;
};

export type EvaluateCouponsOptions = RecomCallOptions & {
	identity: RecomIdentity;
	partnumber?: string;
	cartTotal?: number;
};

export const evaluateCoupons = async ({
	identity,
	partnumber,
	cartTotal,
	...options
}: EvaluateCouponsOptions): Promise<CouponEvaluateData> => {
	const { data } = await call<CouponEvaluateData>(options, 'POST', recomPaths.evaluateCoupons(), {
		...resolveRecomIdentity(identity),
		partnumber,
		cartTotal,
	});
	return data;
};

export const recomApi = {
	getRecommendation,
	getRecentlyViewed,
	getTrendingProducts,
	getPopularProducts,
	recordUserEvents,
	mergeUserSession,
	submitUserQuiz,
	updateUserProfile,
	evaluateCoupons,
};
